<template>
  <div class="signup-wrapper">
    <div class="signup-container">
      <h2>Sign up</h2>
      <div class="signup-input-container">
        <label for="registration-code">Registration code</label>
        <span class="label-hint">Ask your organization admin!</span>
        <input class="input" id="registration-code" type="text" v-model="newUser.registration_code"
        @focus="showEmptyRegistrationCodeError = false">
        <span v-if="showEmptyRegistrationCodeError"
              class="input-error">Registration code cannot be empty.</span>
      </div>
      <div class="signup-input-container">
        <label for="username">Username</label>
        <span class="label-hint">Must be unique.</span>
        <input class="input" id="username" type="text" v-model="newUser.username"
               @focus="turnOffUsernameErrors" @blur="checkUniqueUsername">
        <span v-if="showUsernameNotUniqueError"
              class="input-error">Username already exists. Please try a different one.</span>
        <span v-if="showEmptyUsernameError"
              class="input-error">Username cannot be empty.</span>
      </div>
      <div class="signup-input-container">
        <label for="password">Password</label>
        <input class="input" id="password" type="password" v-model="newUser.password"
        @focus="turnOffPasswordErrors">
        <span v-if="showEmptyPasswordError"
              class="input-error">Password cannot be empty.</span>
      </div>
      <div class="signup-input-container">
        <label for="password-repeat">Repeat password</label>
        <input class="input" id="password-repeat" type="password" v-model="newUser.password_repeat"
        @focus="turnOffPasswordErrors">
        <span v-if="showPasswordsNotEqualError"
              class="input-error">Passwords must be equal.</span>
      </div>
      <span v-if="showSignupSuccess" class="signup-success">Sign up successful! Redirecting...</span>
      <span v-if="showSignupError" class="input-error">{{ signupErrorMessage }}</span>
      <button class="signup-button" @click="submitSignup">Sign up</button>
    </div>
  </div>
</template>

<script setup>

import {ref} from "vue";
import apiService from "@/services/apiService";
import router from "@/router";

const newUser = ref({
  username: "",
  password: "",
  password_repeat: "",
  registration_code: "",
});
const showUsernameNotUniqueError = ref(false);
const showPasswordsNotEqualError = ref(false);
const showEmptyRegistrationCodeError = ref(false);
const showEmptyUsernameError = ref(false);
const showEmptyPasswordError = ref(false);
const showSignupSuccess = ref(false);
const showSignupError = ref(false);
const signupErrorMessage = ref('');

async function checkUniqueUsername() {
  if (newUser.value.username !== "") {
    const response = await apiService.checkUniqueUsername(newUser.value.username);
    if (!response.ok) {
      showUsernameNotUniqueError.value = true;
    }
  }
}

function turnOffUsernameErrors() {
  showEmptyUsernameError.value = false;
  showUsernameNotUniqueError.value = false;
}

function turnOffPasswordErrors() {
  showEmptyPasswordError.value = false;
  showPasswordsNotEqualError.value = false;
}

async function submitSignup() {
  if (newUser.value.registration_code === "") {
    showEmptyRegistrationCodeError.value = true;
    return;
  }
  if (newUser.value.username === "") {
    showEmptyUsernameError.value = true;
    return;
  }
  if (newUser.value.password === "") {
    showEmptyPasswordError.value = true;
    return;
  }
  if (newUser.value.password !== newUser.value.password_repeat) {
    showPasswordsNotEqualError.value = true;
    return;
  }

  if (showUsernameNotUniqueError.value) {
    return;
  }

  const response = await apiService.submitSignup(newUser.value);
  if (response.user_id) {
    showSignupSuccess.value = true;
    await redirectToLogin()
  } else {
    signupErrorMessage.value = response.message;
    showSignupError.value = true;
  }
}

async function redirectToLogin() {
  setTimeout(() => {
    router.push("/login");
  }, 3000)
}

</script>

<style scoped>
.signup-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: linear-gradient(
      rgb(0, 0, 0, 0.5),
      rgb(0, 0, 0, 0.5)),
  url('/public/signup-image.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 0.5rem;
  box-sizing: border-box;
}

.signup-container {
  background-color: white;
  padding: 2rem 3rem 3rem 3rem;
  width: 300px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.signup-input-container {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
}

.signup-input-container:first-of-type {
  border-bottom: 1px solid lightgray;
  padding-bottom: 1rem;
}

.input {
  width: 100%;
  box-sizing: border-box;
}

.label-hint {
  font-size: 0.7rem;
  color: gray;
}

.signup-button {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: bold;
  cursor: pointer;
  background-color: #1a3968;

  transition: transform 0.2s ease;
}

.signup-button:hover {
  animation: rainbow-cycle 0.75s linear infinite;
  transform: scale(1.05);
}

@keyframes rainbow-cycle {
  0% {
    background-color: #ff595e;
  }
  16% {
    background-color: #ff924c;
  }
  32% {
    background-color: #ffca3a;
  }
  48% {
    background-color: #8ac926;
  }
  64% {
    background-color: #1982c4;
  }
  80% {
    background-color: #6a4c93;
  }
  100% {
    background-color: #ff595e;
  }
}

.input-error {
  font-size: 0.9rem;
  background-color: #f4c2c2;
  color: darkred;
  padding: 0.3rem;
  border-radius: 10px;
}

.signup-success {
  font-size: 0.9rem;
  background-color: #d1f4b0;
  color: #156100;
  padding: 1rem;
  border-radius: 10px;
}
</style>